import { Nullish, Required } from "utility-types";
import { ZipInput } from "../shared/zip";
import { differenceInSeconds, isToday } from "date-fns";
import { CompletionProgress } from "../shared/queue";
import { isNil } from "lodash";
import { formatBytes } from "../engine";
import { ArchiverProgress } from "../shared/data_download";
import { ZipJobLog, ZipJobLogResultSuccess } from "../shared/mongo/zip_job_log";
import { DataDownloadFlowJobLog } from "../shared/mongo/data_download_flow_job_log";
import { IdentityType } from "../shared/db";

export interface DownloadJob {
  id: string;
  createdAt: Date;
  state: string;
  data: {
    inputHash: string;
    input: ZipInput;
  };
}

export interface UserDownloadInfo {
  projectId: string;
  participantIds: string[];
  startDate?: Date;
  endDate?: Date;
}

export type UserDownloadProgress = DataDownloadFlowProgress;

export type UserDownloadResult = UserDownloadResultSuccess | UserDownloadResultError | null;

export interface UserDownload<Result extends UserDownloadResult = UserDownloadResult> {
  info: UserDownloadInfo;
  result: Result;
  progress: UserDownloadProgress | null;
  startedAt?: Date | null;
}

export interface UserDownloadState {
  isUserDownloadIdle: boolean;
  isUserDownloadInProgress: boolean;
  isUserDownloadSuccess: boolean;
  isUserDownloadError: boolean;
  userDownload: UserDownload | null;
  userDownloadInfo: UserDownloadInfo | null;
  userDownloadProgress: UserDownloadProgress | null;
  successUserDownload: UserDownload<UserDownloadResultSuccess> | null;
  errorUserDownload: UserDownload<UserDownloadResultError> | null;
  successUserDownloadResult: UserDownloadResultSuccess | null;
  errorUserDownloadResult: UserDownloadResultError | null;
  downloaded: boolean;
}

export interface RequesterSummary {
  id: string;
  type: IdentityType;
  email: string;
}

export interface ProjectDataDownloadFlowMetadata
  extends
    Omit<DataDownloadFlowJobLog, "_id" | "version" | "requesterId" | "registeredAt">,
    Partial<Pick<DataDownloadFlowJobLog, "registeredAt">>,
    Partial<Pick<ZipJobLog, "downloadedAt" | "expireAt">>,
    Pick<
      ZipJobLogResultSuccess,
      "compressedSizeInBytes" | "uncompressedSizeInBytes" | "partsBreakdown"
    > {
  id: string;
  isExpired?: boolean;
  url?: string | string[];
  requester?: RequesterSummary;
}

export function useUserDownload(userDownload: UserDownload | Nullish): UserDownloadState {
  const userDownloadInfo = userDownload?.info ?? null;
  const userDownloadProgress = userDownload?.progress ?? null;
  if (isUserDownloadInProgress(userDownload)) {
    return {
      isUserDownloadIdle: false,
      isUserDownloadInProgress: true,
      isUserDownloadSuccess: false,
      isUserDownloadError: false,
      successUserDownload: null,
      errorUserDownload: null,
      successUserDownloadResult: null,
      errorUserDownloadResult: null,
      userDownload: userDownload ?? null,
      userDownloadInfo,
      userDownloadProgress,
      downloaded: false,
    };
  }
  if (isUserDownloadSuccess(userDownload)) {
    return {
      isUserDownloadIdle: false,
      isUserDownloadInProgress: false,
      isUserDownloadSuccess: true,
      isUserDownloadError: false,
      successUserDownload: userDownload,
      errorUserDownload: null,
      successUserDownloadResult: userDownload.result,
      errorUserDownloadResult: null,
      userDownload: userDownload ?? null,
      userDownloadInfo,
      userDownloadProgress,
      downloaded: !!userDownload.result.downloadedAt,
    };
  }
  if (isUserDownloadError(userDownload)) {
    return {
      isUserDownloadIdle: false,
      isUserDownloadInProgress: false,
      isUserDownloadSuccess: false,
      isUserDownloadError: true,
      successUserDownload: null,
      errorUserDownload: userDownload,
      successUserDownloadResult: null,
      errorUserDownloadResult: userDownload.result,
      userDownload: userDownload ?? null,
      userDownloadInfo,
      userDownloadProgress,
      downloaded: false,
    };
  }
  return {
    isUserDownloadIdle: true,
    isUserDownloadInProgress: false,
    isUserDownloadSuccess: false,
    isUserDownloadError: false,
    successUserDownload: null,
    errorUserDownload: null,
    successUserDownloadResult: null,
    errorUserDownloadResult: null,
    userDownload: userDownload ?? null,
    userDownloadInfo,
    userDownloadProgress,
    downloaded: false,
  };
}

export function isUserDownloadInProgress(download: UserDownload | Nullish): boolean {
  if (download) {
    if (!download.result) {
      return true;
    }
  }
  return false;
}

export function isUserDownloadSuccess(
  download: UserDownload | Nullish,
): download is UserDownload<UserDownloadResultSuccess> {
  if (download) {
    if (download.result) {
      return isUserDownloadResultSuccess(download.result);
    }
  }
  return false;
}

export function isUserDownloadError(
  download: UserDownload | Nullish,
): download is UserDownload<UserDownloadResultError> {
  if (download) {
    const { result } = download;
    if (result) {
      return isUserDownloadResultError(result);
    }
  }
  return false;
}

export interface UserDownloadResultSuccess
  extends
    Pick<DataDownloadFlowJobLog, "createdAt" | "executionStart" | "inputHash">,
    Partial<Pick<DataDownloadFlowJobLog, "registeredAt">>,
    Pick<ZipJobLog, "downloadedAt">,
    Pick<
      ZipJobLogResultSuccess,
      "compressedSizeInBytes" | "uncompressedSizeInBytes" | "partsBreakdown"
    > {
  url: string | string[];
  requester?: RequesterSummary;
}

export function isUserDownloadResultSuccess(
  result: UserDownloadResult,
): result is UserDownloadResultSuccess {
  if (result) {
    return "url" in result;
  }
  return false;
}

export interface UserDownloadResultError {
  errorMessage: string;
}

export function isUserDownloadResultError(
  result: UserDownloadResult,
): result is UserDownloadResultError {
  if (result) {
    return "errorMessage" in result;
  }
  return false;
}

export function jobDataToUserDownloadInfo(
  data: Required<Partial<DataDownloadFlowJobData>, "input">,
): UserDownloadInfo {
  return {
    projectId: data.input.projectId,
    participantIds: [...data.input.participantIds].sort(),
    startDate: data.input.startDateIso ? new Date(data.input.startDateIso) : undefined,
    endDate: data.input.endDateIso ? new Date(data.input.endDateIso) : undefined,
  };
}

export function metadataToUserDownloadInfo(
  metadata: Pick<ProjectDataDownloadFlowMetadata, "input">,
): UserDownloadInfo {
  return jobDataToUserDownloadInfo({ input: metadata.input });
}

export function metadataToSuccessUserDownload(
  metadata: ProjectDataDownloadFlowMetadata,
): UserDownload<UserDownloadResultSuccess> | null {
  if (!metadata.success || metadata.failure || !metadata.url || metadata.isExpired) return null;

  return {
    info: metadataToUserDownloadInfo(metadata),
    result: {
      inputHash: metadata.inputHash,
      url: metadata.url,
      createdAt: metadata.createdAt,
      registeredAt: metadata.registeredAt,
      downloadedAt: metadata.downloadedAt,
      executionStart: metadata.executionStart,
      compressedSizeInBytes: metadata.compressedSizeInBytes,
      uncompressedSizeInBytes: metadata.uncompressedSizeInBytes,
      partsBreakdown: metadata.partsBreakdown,
      requester: metadata.requester,
    },
    progress: null,
    startedAt: metadata.registeredAt,
  };
}

export class UserDownloadInfoDescription {
  constructor(private userDownload: Required<Partial<UserDownload>, "info">) {}

  get timeRange(): string {
    const {
      info: { startDate, endDate },
    } = this.userDownload;
    let startDateString: string | undefined;
    let endDateString: string | undefined;
    if (startDate) {
      startDateString = this.getUTCDateString(new Date(startDate));
    }
    if (endDate) {
      endDateString = this.getUTCDateString(new Date(endDate));
    }
    if (startDateString && endDateString) {
      return `${startDateString} - ${endDateString}`;
    }
    if (endDateString) {
      return `- ${endDateString}`;
    }
    if (startDateString) {
      return `${startDateString} - Now`;
    }
    return `- Now`;
  }

  get participants(): string {
    const {
      info: { participantIds },
    } = this.userDownload;
    const count = participantIds.length;
    return `${count} participant${count === 1 ? "" : "s"} selected`;
  }

  get fileCreated():
    | {
        absolute: string;
        relative: string;
        moreThan2Hours: boolean;
      }
    | undefined {
    const result = this.userDownload.result;
    if (!result || !isUserDownloadResultSuccess(result)) return;
    const { createdAt } = result;
    const secondsToNow = differenceInSeconds(new Date(), new Date(createdAt));
    return {
      absolute: this.getDateTimeString(new Date(createdAt)),
      relative: `${this.getRelativeTimeString(secondsToNow)} ago`,
      moreThan2Hours: secondsToNow > 7200,
    };
  }

  get preparationTime(): string | undefined {
    const result = this.userDownload.result;
    if (!result || !isUserDownloadResultSuccess(result)) return;
    const { createdAt, executionStart } = result;
    const { startedAt } = this.userDownload;
    const start = new Date(startedAt ?? executionStart);
    const end = new Date(createdAt);
    const seconds = differenceInSeconds(end, start);
    return this.getRelativeTimeString(seconds, {
      full: true,
      plural: true,
    });
  }

  get fileSize():
    | {
        compressed: string;
        uncompressed: string;
      }
    | undefined {
    const result = this.userDownload.result;
    if (!result || !isUserDownloadResultSuccess(result)) return;

    const { compressedSizeInBytes, uncompressedSizeInBytes } = result;
    if (isNil(compressedSizeInBytes) || isNil(uncompressedSizeInBytes)) {
      return;
    }
    return {
      compressed: formatBytes(compressedSizeInBytes),
      uncompressed: formatBytes(uncompressedSizeInBytes),
    };
  }

  get fileParts(): string | undefined {
    const result = this.userDownload.result;
    if (!result || !isUserDownloadResultSuccess(result)) return;

    const partCount = Array.isArray(result.url) ? result.url.length : 1;
    if (partCount <= 1) return;
    return `${partCount} parts`;
  }

  private getDateString(date: Date): string {
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  private getUTCDateString(date: Date): string {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  private getTimeString(date: Date): string {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true });
  }

  private getDateTimeString(date: Date): string {
    if (isToday(date)) {
      return this.getTimeString(date);
    }
    return `${this.getDateString(date)} ${this.getTimeString(date)}`;
  }

  private getRelativeTimeString(
    seconds: number,
    options: {
      full?: boolean;
      plural?: boolean;
    } = {},
  ): string {
    const { full = false, plural = false } = options;
    const hours = Math.floor(seconds / 60 / 60);
    const minutes = Math.floor(seconds / 60) % 60;
    const minutesString = `${minutes} ${full ? "minute" : "min"}${plural ? (minutes === 1 ? "" : "s") : ""}`;
    if (hours === 0) {
      return minutesString;
    }
    const hoursString = `${hours} ${full ? "hour" : "hr"}${plural ? (hours === 1 ? "" : "s") : ""}`;
    if (minutes === 0) {
      return hoursString;
    }
    return `${hoursString} ${minutesString}`;
  }
}

export interface DataDownloadFlowJobData {
  input: ZipInput;
  inputHash: string;
  registeredAt: number;
  force?: boolean;
  requesterId?: string;
}

export interface DataDownloadFlowProgress {
  tabulatorProgress?: CompletionProgress;
  zipProgress?: ArchiverProgress;
}
